import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, ChevronDown, EyeOff, FileClock, Flame, MessageSquareWarning, Radar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIGNAL_META = {
  stale_review: { icon: FileClock, label: "Stale review" },
  silent_client: { icon: EyeOff, label: "Silent client" },
  changes_requested: { icon: MessageSquareWarning, label: "Changes requested" },
  unseen_document: { icon: AlertTriangle, label: "Unseen document" },
  hot_opportunity: { icon: Flame, label: "Hot opportunity" },
};

const SEVERITY_DOT = {
  high: "bg-[#E53935]",
  medium: "bg-amber-300",
  low: "bg-white/30",
};

// Ida's Radar: nothing slips. Signals ranked by urgency, each one click from action.
export default function IdaRadar() {
  const [signals, setSignals] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () =>
      axios.get(`${API}/hq/radar`).then(({ data }) => setSignals(data.signals || [])).catch(() => setSignals((s) => s || []));
    load();
    const t = setInterval(load, 60000); // the radar never sleeps
    return () => clearInterval(t);
  }, []);

  if (signals === null) return null;

  const shown = expanded ? signals : signals.slice(0, 5);
  const highCount = signals.filter((s) => s.severity === "high").length;

  return (
    <div className="mt-6 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5 md:p-6" data-testid="hq-radar">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-white/40">
          <Radar className="w-3.5 h-3.5" /> Ida&rsquo;s Radar
        </div>
        <span className="text-[11.5px] text-white/35" data-testid="hq-radar-count">
          {signals.length === 0
            ? "All clear"
            : `${signals.length} signal${signals.length > 1 ? "s" : ""}${highCount ? ` · ${highCount} urgent` : ""}`}
        </span>
      </div>

      {signals.length === 0 ? (
        <p className="mt-4 text-white/40 text-[13px]" data-testid="hq-radar-clear">
          Nothing slipping. Every client has movement, every document has an answer.
        </p>
      ) : (
        <div className="mt-4 space-y-1.5" data-testid="hq-radar-list">
          {shown.map((s, i) => {
            const meta = SIGNAL_META[s.type] || SIGNAL_META.silent_client;
            const Icon = meta.icon;
            return (
              <button
                key={`${s.project_id}-${s.type}-${i}`}
                type="button"
                onClick={() => navigate(`/hq/projects/${s.project_id}`)}
                className="w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-xl hover:bg-white/[0.05] transition-colors group"
                data-testid={`hq-radar-signal-${s.type}`}
              >
                <span className={`mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 ${SEVERITY_DOT[s.severity] || SEVERITY_DOT.low}`} />
                <Icon className="w-3.5 h-3.5 mt-[3px] text-white/30 group-hover:text-white/60 shrink-0 transition-colors" />
                <span className="flex-1 min-w-0">
                  <span className="block text-white/80 text-[13px] leading-[1.45]">
                    <span className="font-semibold">{s.title}</span>
                    <span className="text-white/35"> · {s.client} · {meta.label}</span>
                  </span>
                  <span className="block text-white/45 text-[12px] leading-[1.55] mt-0.5">{s.line}</span>
                </span>
              </button>
            );
          })}
          {signals.length > 5 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 inline-flex items-center gap-1.5 text-[12px] text-white/40 hover:text-white/70 transition-colors px-3"
              data-testid="hq-radar-toggle"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
              {expanded ? "Show less" : `Show all ${signals.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
